import React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import CustomButton, {
  CustomButtonVariant,
} from "../CustomButton/CustomButton";

export interface HeaderItem {
  label: string;
  value: string;
  flex?: number;
}

export interface HighlightCardItem {
  label: string;
  value: string;
}

export interface HighlightCard {
  items: HighlightCardItem[];
}

export interface DetailItem {
  label: string;
  value: string;
  columns?: number;
}

export interface ScheduleDetailsModalAction {
  label: string;
  variant?: CustomButtonVariant;
  onClick?: () => void;
}

interface ScheduleDetailsModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  header: HeaderItem[];
  highlightCards?: HighlightCard[];
  details?: DetailItem[];
  footer?: {
    label: string;
    value: string;
  };
  actions?: ScheduleDetailsModalAction[];
}

const ScheduleDetailsModal: React.FC<ScheduleDetailsModalProps> = ({
  open,
  onClose,
  title,
  header,
  highlightCards,
  details,
  footer,
  actions,
}) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: 18,
          color: "shadesOfDark.dark",
          pb: 1,
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent sx={{ pt: 1, overflowX: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 2,
            mb: 3,
            p: 2,
            border: "1px solid #E0E0E0",
            borderRadius: 1,
          }}
        >
          {header.map((item, index) => (
            <Box
              key={index}
              sx={{
                flex: item.flex ?? undefined,
                minWidth: item.flex ? 120 : undefined,
              }}
            >
              <Typography fontSize={12} color="text.secondary">
                {item.label}
              </Typography>
              <Typography fontSize={14} fontWeight={600}>
                {item.value || "-"}
              </Typography>
            </Box>
          ))}
        </Box>

        {highlightCards && highlightCards.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {highlightCards.map((card, cardIndex) => (
              <Grid item xs={12} sm={6} key={cardIndex}>
                <Box
                  sx={{
                    backgroundColor: "#F5F5F5",
                    borderRadius: 1,
                    p: 2,
                    height: "100%",
                  }}
                >
                  {card.items.map((item, itemIndex) => (
                    <Box key={itemIndex} sx={{ mt: itemIndex > 0 ? 1.5 : 0 }}>
                      <Typography fontSize={12} color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography fontSize={14} fontWeight={600}>
                        {item.value || "-"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {details && details.length > 0 && (
          <Box
            sx={{
              border: "1px solid #E0E0E0",
              borderRadius: 1,
              p: 2,
              mb: 3,
            }}
          >
            <Grid container spacing={2}>
              {details.map((item, index) => (
                <Grid item xs={6} sm={item.columns ?? 4} key={index}>
                  <Typography fontSize={12} color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography
                    fontSize={14}
                    fontWeight={600}
                    sx={{ wordBreak: "break-word" }}
                  >
                    {item.value || "-"}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {footer && (
          <Box>
            <Typography fontSize={14} fontWeight={700}>
              {footer.label}
            </Typography>
            <Typography
              fontSize={14}
              color="text.secondary"
              sx={{
                mt: 0.5,
                wordBreak: "break-word",
                maxHeight: 120,
                overflowY: "auto",
              }}
            >
              {footer.value || "-"}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {actions?.map((action, index) => (
          <CustomButton
            key={index}
            title={action.label}
            onClick={action.onClick}
            variant={action.variant}
          />
        ))}
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleDetailsModal;
